import { Injectable } from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  BenchmarkDataPrimaryLookupService,
  BmarkSpotRiskTableDefinitionFactory,
  CcyLookupService,
  CcyPromiseLookupService,
  HedgeOrderDataPrimaryLookupService,
  ProfilePromiseActionService,
  Roles,
  SpotRiskModel,
  SystemConfigPromiseService,
  TraderGroupLookupService,
  TraderGroupPromiseLookupService,
} from "shared/cubiq-services";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { ActivatedRoute } from "@angular/router";
import { BmarkBaseRiskDataService } from "app/bfx-bmark-summary/blotters/spot-risk/services/bmark-base-risk-data.service";
import { BmarkFixingsManagerService } from "app/bfx-bmark-summary/services/bmark-fixings-manager.service";
import { BenchmarkPositionLookupService } from "shared/cubiq-services/lookup/model/impl/BenchmarkPositionLookupService";
import { SpotRiskPair } from "shared/cubiq-services/lookup/model/impl/bmrisk/SpotRiskModel";
import { BLOTTER_RELOAD_TYPE } from "shared/cubiq-services/lookup/model/impl/bmrisk/BaseBMRiskModel";
import { BmarkRiskDisplayModel } from "shared/cubiq-services/model/benchmarks/BmarkRiskDisplay.model";
import { BmarkSpotRiskDisplayModel } from "shared/cubiq-services/model/benchmarks/BmarkSpotRiskDisplay.model";
import { BmarkClientOrdersDataService } from "../../client-orders/services/bmark-client-orders-data.service";

/**
 * Service providing aggregate risk data model to benchmark Spot risk blotter
 */
@Injectable()
export class BmarkSpotRiskDataService extends BmarkBaseRiskDataService {
  /**
   * Actual data model doing the aggregation
   */
  public spotRiskModel: SpotRiskModel;

  constructor(
    dataService: BenchmarkDataPrimaryLookupService,
    hedgingDataService: HedgeOrderDataPrimaryLookupService,
    tableDefinitionService: BmarkSpotRiskTableDefinitionFactory,
    bmarkManager: BmarkFixingsManagerService,
    profileActionService: ProfilePromiseActionService,
    clientOrdersDataService: BmarkClientOrdersDataService,
    traderGroupLookup: TraderGroupPromiseLookupService,
    ccyService: CcyLookupService,
    _traderGroupLookup: TraderGroupLookupService,
    _ccyLookupService: CcyPromiseLookupService,
    cmdLine: ActivatedRoute,
    positionService: BenchmarkPositionLookupService,
    sysConfigLookup: SystemConfigPromiseService,
  ) {
    super(
      dataService,
      hedgingDataService,
      tableDefinitionService,
      bmarkManager,
      profileActionService,
      clientOrdersDataService,
      traderGroupLookup,
      ccyService,
      _traderGroupLookup,
      _ccyLookupService,
      cmdLine,
      positionService,
      sysConfigLookup,
    );
  }

  /**
   * Called during constructor so initialisers at this level will not yet be called
   */
  protected override initialize() {
    // Ensure no subscriptons made user not authorized
    const userEnabled = this.sysConfigLookup.hasRole(Roles.ROLE_VIEW_BENCHMARK_SUMMARY_RISK);

    // Spot Risk Model
    this.spotRiskModel = new SpotRiskModel(
      this.clientOrdersDataService,
      this.baseTraderGroupLookup,
      this.ccyService,
      userEnabled,
      this.cmdLine,
    );

    userEnabled.then((isEnabled) => {
      // Only want to maintain the data model if the user is authorised to see it
      if (isEnabled) {
        this.spotRiskModel.subscribe(this);

        this.dataService.isPrimed().then((primed: boolean) => {
          this.createListOfCcyMaterials();
          // Fixing Manager
          this.bmarkManager.displayFixing$.subscribe(this.onFixingManagerEvent.bind(this));
          // Fixing Lookup
          this.dataService.fixingModelUpdateSubject.subscribe(this.onFixingModelUpdate.bind(this));
          this.positionService.changePositionSubject.subscribe(
            this.onPositionModeChange.bind(this),
          );
        });
      }
    });
  }

  /**
   *
   * @param reason
   * @param forceRepaint
   */
  protected override tableDataReload(
    reason?: BLOTTER_RELOAD_TYPE,
    forceRepaint: boolean = false,
  ): void {
    logger.out.info(
      `bmsprd-s: tableDataReload() -> RELOAD | Data: reason= ${BLOTTER_RELOAD_TYPE[reason]}`,
    );
    this._rawData = Object.create(null);

    this.blotterChangeSubject.next(reason);
    if (
      this.spotRiskModel.spotRiskData instanceof Map &&
      this.spotRiskModel.spotRiskData.size > 0
    ) {
      let data = this.processRiskData(this.spotRiskModel.spotRiskData);
      //logger.out.warn(`BMARK: SpotRisk: tableDataReload() -> RELOAD |  Transformed Data:`, data);
      super.onFullUpdateEvent(data, forceRepaint);
    } else {
      super.onFullUpdateEvent([], forceRepaint);
    }
  }

  protected tableDataUpdateRows(ccyPairIds: Array<string>, forceRepaint: boolean = false): void {
    if (Array.isArray(ccyPairIds) && ccyPairIds.length > 0) {
      // delete the rows which are going to be updated
      this.tableDataDeleteRows(ccyPairIds);
      //
      //logger.out.info(`BMARK: SpotRiskData: tableDataUpdateRows() -> UPDATE | row: `, ccyPairIds.toString());
      // get the data
      let ccyPairsUpdateData: Map<string, SpotRiskPair> = new Map<string, SpotRiskPair>();
      for (let ccyPair of ccyPairIds) {
        if (this.spotRiskModel.spotRiskData.has(ccyPair)) {
          ccyPairsUpdateData.set(ccyPair, this.spotRiskModel.spotRiskData.get(ccyPair));
        }
      }
      // do the update (add the rows)
      if (ccyPairsUpdateData.size > 0) {
        this.onFullUpdateEvent(this.processRiskData(ccyPairsUpdateData), forceRepaint);
      }
    }
  }

  protected getAlertThresholds(fixingId: string): Map<string, CubiqTS.BigNumber> {
    return this.dataService.getSpotAlertThresholds(fixingId);
  }
  protected getWarningThresholds(fixingId: string): Map<string, CubiqTS.BigNumber> {
    return this.dataService.getSpotWarningThresholds(fixingId);
  }

  protected createDisplayModel(key: string, value: any): BmarkRiskDisplayModel {
    return new BmarkSpotRiskDisplayModel(this.activeFixingId + key, value);
  }
}
